/**
 * zod-utils の目的
 * - Zod の検証結果・エラーを UI 表示用の形に集約する共通ユーティリティを提供する。
 * - `safeParse` の成功/失敗を統一フォーマットで扱えるようにし、フォーム側の重複実装を削減する。
 * - OpenAPI クライアント応答（{status, data}）を Zod で安全に取り出すヘルパーを提供し、画面間での処理の一貫性を高める。
 */
import type { ZodError, ZodIssue } from "zod";

/**
 * ZodError -> Record<field, message>
 * 使い道:
 * - `schema.safeParse(data)` が失敗したとき、`ZodError` の `issues` をフィールド単位のメッセージへ整形します。
 * - フォームの `errors` ステートへそのまま投入できる形に変換します。
 * 使い方例:
 * ```ts
 * const result = schema.safeParse(input);
 * if (!result.success) {
 *   const fieldErrors = mapZodIssues(result.error);
 *   setErrors(fieldErrors); // { postalCode: "郵便番号の形式が正しくありません", ... }
 * }
 * ```
 */
export function mapZodIssues(error: ZodError): Record<string, string> {
  const out: Record<string, string> = {};
  for (const issue of error.issues as ZodIssue[]) {
    const path = issue.path?.[0];
    if (typeof path === "string" && !out[path]) {
      out[path] = issue.message;
    }
  }
  return out;
}

/**
 * safeParse ラッパー
 * 使い道:
 * - Zodの `safeParse` をラップし、成功時は `{ success: true, data }`、失敗時は `{ success: false, errors }` を返します。
 * - フォーム側で条件分岐を簡潔にし、失敗時のフィールドエラー整形を共通化します。
 * 使い方例:
 * ```ts
 * const res = validateWithZod(AddressInputSchema, input);
 * if (res.success) {
 *   // res.data を利用して送信
 * } else {
 *   setErrors(res.errors);
 * }
 * ```
 */
export function validateWithZod<T>(
  schema: {
    safeParse: (
      data: unknown,
    ) => {
      success: boolean;
      data?: T;
      error?: ZodError;
    };
  },
  data: unknown,
): { success: true; data: T } | {
  success: false;
  errors: Record<string, string>;
} {
  const parsed = schema.safeParse(data);
  if (parsed.success) {
    return { success: true, data: parsed.data as T };
  }
  const errors = parsed.error ? mapZodIssues(parsed.error as ZodError) : {};
  return { success: false, errors };
}

// OpenAPIクライアントの {status, data} 応答から、Zodで成功時のみ値を返す
export type SchemaLike<T,> = {
  safeParse: (data: unknown) => { success: boolean; data?: T };
};

/**
 * parseOk
 * 使い道:
 * - OpenAPIクライアントの応答（`{ status, data }`）から、`status === 200` のときに Zod でパースして値を返します。
 * - 失敗時（200以外 or パース失敗）は `null` を返し、画面側の分岐を簡潔にします。
 * 使い方例（Reactコンポーネント内）:
 * ```ts
 * const detail = React.useMemo(
 *   () => parseOk(detailQuery.data, ZPatientDetail),
 *   [detailQuery.data],
 * );
 * if (!detail) { /* ローディングや非表示など
 **/
export function parseOk<T>(resp: unknown, schema: SchemaLike<T>): T | null {
  if (!resp || typeof resp !== "object" || !("status" in (resp as any))) {
    return null;
  }
  const r = resp as { status?: number; data?: unknown };
  if (r.status !== 200) return null;
  const parsed = schema.safeParse(r.data);
  return parsed.success && parsed.data !== undefined
    ? (parsed.data as T)
    : null;
}
